import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CNetActions } from '../other/_cnet';
import { fmt, PieChart, BarChart, COMPOUND_OPTIONS, effectiveAnnual } from './_finHelpers';

// Contribution frequency options (per-year multiplier)
const CONTRIB_FREQS = [
  { key: 'continuously', label: 'per year (continuously)', ppy: Infinity },
  { key: 'week',         label: 'per week',                ppy: 52 },
  { key: 'biweek',       label: 'per 2 weeks',             ppy: 26 },
  { key: 'semimonth',    label: 'semi-monthly',            ppy: 24 },
  { key: 'month',        label: 'per month',               ppy: 12 },
  { key: 'quarter',      label: 'per quarter',             ppy: 4 },
  { key: 'semiannual',   label: 'semi-annually',           ppy: 2 },
  { key: 'year',         label: 'per year',                ppy: 1 },
];

/* Build accumulation schedule + summary given all 4 solved variables */
function buildSchedule({ start, years, contrib, rPer, ppy, contribWhen }) {
  const rows = [];
  let bal = start;
  const yInt = years > 0 ? years : 0;
  for (let y = 1; y <= yInt; y++) {
    let yearInt = 0, yearContrib = 0;
    for (let p = 0; p < ppy; p++) {
      if (contribWhen === 'beginning') { bal += contrib; yearContrib += contrib; }
      const i = bal * rPer;
      yearInt += i; bal += i;
      if (contribWhen === 'end') { bal += contrib; yearContrib += contrib; }
    }
    rows.push({ year: y, deposit: yearContrib, interest: yearInt, balance: bal });
  }
  const totalContrib = rows.reduce((a,r)=>a+r.deposit,0);
  const totalInterest = rows.reduce((a,r)=>a+r.interest,0);
  return { rows, finalBalance: bal, totalContrib, totalInterest };
}

export default function InvestmentCalc() {
  const [tab, setTab] = useState('end');
  const [start, setStart] = useState(20000);
  const [years, setYears] = useState(10);
  const [rate, setRate] = useState(6);
  const [compound, setCompound] = useState('annually');
  const [contrib, setContrib] = useState(1000);
  const [contribFreq, setContribFreq] = useState('year');
  const [contribWhen, setContribWhen] = useState('end');
  const [endTarget, setEndTarget] = useState(200000);
  const [showResult, setShowResult] = useState(true);
  const doCalc = () => setShowResult(true);
  const doClear = () => {
    setTab('end'); setStart(20000); setContrib(1000); setContribFreq('monthly');
    setYears(10); setRate(6); setCompound('annually'); setContribWhen('end'); setEndTarget(200000);
    setShowResult(false);
  };

  const contribOpt = CONTRIB_FREQS.find(o => o.key === contribFreq) || CONTRIB_FREQS[7];
  const contribPpy = contribOpt.ppy;

  const c = useMemo(() => {
    const ppy = contribPpy === Infinity ? 12 : contribPpy;
    const compoundEffAnnual = effectiveAnnual(rate, compound);
    const rPer = Math.pow(1 + compoundEffAnnual, 1 / ppy) - 1;
    const timing = contribWhen === 'beginning' ? 1 : 0;

    // Resolve the unknown → produce a full set of 4 known values (S, Y, R, C)
    let S = start, Y = years, R = rPer, C = contrib, computedRate = rate;
    let solved = { label: 'End Balance', value: 0, extra: null };

    if (tab === 'end') {
      // All 4 inputs given → schedule uses them; final = accumulated
    } else if (tab === 'starting') {
      const N = years * ppy;
      const factor = Math.pow(1 + rPer, N);
      const contribFV = rPer === 0 ? contrib * N : contrib * ((factor - 1)/rPer) * (1 + rPer*timing);
      S = Math.max(0, (endTarget - contribFV) / factor);
      solved = { label: 'Required Starting Amount', value: S };
    } else if (tab === 'contrib') {
      const N = years * ppy;
      const factor = Math.pow(1 + rPer, N);
      C = rPer === 0
        ? (endTarget - start * factor)/N
        : (endTarget - start * factor) / (((factor - 1)/rPer) * (1 + rPer*timing));
      solved = { label: `Required Additional Contribution (${contribOpt.label})`, value: C };
    } else if (tab === 'rate') {
      let lo = 0, hi = 1;
      for (let i = 0; i < 80; i++) {
        const mid = (lo + hi) / 2;
        const Rp = Math.pow(1 + mid, 1/ppy) - 1;
        const N = years * ppy;
        const factor = Math.pow(1 + Rp, N);
        const val = start * factor + (Rp === 0 ? contrib * N : contrib * ((factor - 1)/Rp) * (1 + Rp*timing));
        if (val < endTarget) lo = mid; else hi = mid;
      }
      computedRate = ((lo + hi)/2) * 100;
      R = Math.pow(1 + (computedRate/100), 1/ppy) - 1;
      solved = { label: 'Required Annual Return Rate', value: computedRate, extra: '%' };
    } else if (tab === 'length') {
      let lo = 0, hi = 100;
      for (let i = 0; i < 80; i++) {
        const mid = (lo + hi) / 2;
        const N = mid * ppy;
        const factor = Math.pow(1 + rPer, N);
        const val = start * factor + (rPer === 0 ? contrib * N : contrib * ((factor - 1)/rPer) * (1 + rPer*timing));
        if (val < endTarget) lo = mid; else hi = mid;
      }
      Y = (lo + hi)/2;
      solved = { label: 'Required Investment Length', value: Y, extra: ' years' };
    }

    // Build accumulation schedule using resolved knowns (rounded years for table)
    const yearsForSchedule = Math.max(1, Math.ceil(Y));
    const sch = buildSchedule({ start: S, years: yearsForSchedule, contrib: C, rPer: R, ppy, contribWhen });
    // For non-end tabs, prorate last year so total ends at target
    if (tab !== 'end' && sch.rows.length && yearsForSchedule > Y) {
      const scale = (Y - (yearsForSchedule - 1)) / 1;
      const last = sch.rows[sch.rows.length - 1];
      last.deposit *= scale;
      last.interest *= scale;
      last.balance = sch.rows.length > 1 ? sch.rows[sch.rows.length - 2].balance + last.deposit + last.interest : S + last.deposit + last.interest;
      sch.finalBalance = last.balance;
      sch.totalContrib = sch.rows.reduce((a,r)=>a+r.deposit,0);
      sch.totalInterest = sch.rows.reduce((a,r)=>a+r.interest,0);
    }

    return { S, Y, C, computedRate, solved, ...sch };
  }, [tab, start, years, rate, compound, contrib, contribFreq, contribPpy, contribWhen, endTarget]);

  const tabs = [
    { key:'end',      label:'End Amount' },
    { key:'contrib',  label:'Additional Contribution' },
    { key:'rate',     label:'Return Rate' },
    { key:'starting', label:'Starting Amount' },
    { key:'length',   label:'Investment Length' },
  ];

  const solvedDisplay = tab === 'rate'
    ? `${c.solved.value.toFixed(3)}%`
    : tab === 'length'
      ? `${c.solved.value.toFixed(2)} years`
      : fmt(c.solved.value || c.finalBalance);

  return (
    <CalcShell showActions={false} look="cnet" title="Investment Calculator" subtitle="Solve for end balance, needed contribution, required rate, starting amount, or investment length."
      about={<><p>Model investments with compound interest, periodic contributions, and different time horizons. This calculator solves for any one variable when the other four are known.</p></>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200 flex-wrap" data-print="inputs">
        {tabs.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab===t.key?'border-blue-700 text-blue-700':'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`inv-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div data-print="inputs">
          {tab !== 'starting' && <div className="field"><label>Starting Amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={start} onChange={e=>setStart(+e.target.value||0)} className="flex-1" data-testid="inv-start"/></div></div>}
          {tab !== 'length' && <div className="field"><label>After</label><div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)} className="flex-1" data-testid="inv-years"/><span className="self-center text-slate-500">years</span></div></div>}
          {tab !== 'rate' && <div className="field"><label>Return Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1" data-testid="inv-rate"/><span className="self-center text-slate-500">%</span></div></div>}
          <div className="field"><label>Compound</label>
            <select value={compound} onChange={e=>setCompound(e.target.value)} data-testid="inv-compound">
              {COMPOUND_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          {/* Contribution frequency — always visible so users can choose per month vs per year on every tab */}
          <div className="field"><label>Contribution frequency</label>
            <div className="flex flex-wrap gap-3 text-sm mt-1">
              <label className="flex items-center gap-1"><input type="radio" name="contribFreq" checked={contribFreq==='month'} onChange={()=>setContribFreq('month')} data-testid="inv-freq-month"/> per month</label>
              <label className="flex items-center gap-1"><input type="radio" name="contribFreq" checked={contribFreq==='year'} onChange={()=>setContribFreq('year')} data-testid="inv-freq-year"/> per year</label>
              <select value={contribFreq} onChange={e=>setContribFreq(e.target.value)} data-testid="inv-contrib-freq" className="!py-1 text-xs">
                {CONTRIB_FREQS.map(o => <option key={o.key} value={o.key}>Other: {o.label}</option>)}
              </select>
            </div>
          </div>
          {tab !== 'contrib' && <div className="field"><label>Additional Contribution</label>
            <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={contrib} onChange={e=>setContrib(+e.target.value||0)} className="flex-1" data-testid="inv-contrib"/></div>
          </div>}
          <div className="field"><label>Contribute at the</label>
            <div className="flex gap-4 text-sm mt-1">
              <label className="flex items-center gap-1"><input type="radio" name="when" checked={contribWhen==='beginning'} onChange={()=>setContribWhen('beginning')}/> beginning</label>
              <label className="flex items-center gap-1"><input type="radio" name="when" checked={contribWhen==='end'} onChange={()=>setContribWhen('end')}/> end</label>
            </div>
          </div>
          {tab !== 'end' && <div className="field"><label>Target End Balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={endTarget} onChange={e=>setEndTarget(+e.target.value||0)} className="flex-1" data-testid="inv-target"/></div></div>}
          <CNetActions onCalc={doCalc} onClear={doClear} testId="investment" />
        </div>

        <div data-print="result">
          {showResult ? (<>
          <div className="result-panel mb-4">
            <div className="result-title">{c.solved.label}</div>
            <div className="result-value" data-testid="inv-primary">{solvedDisplay}</div>
          </div>
          <table className="data-table"><tbody>
            <tr><td>Starting Amount</td><td className="text-right">{fmt(c.S)}</td></tr>
            <tr><td>Total Contributions</td><td className="text-right">{fmt(c.totalContrib)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.totalInterest)}</td></tr>
            <tr><td className="font-semibold">End Balance</td><td className="text-right font-bold">{fmt(c.finalBalance)}</td></tr>
          </tbody></table>
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Breakdown</h4>
            <PieChart
              parts={[
                { label: 'Starting Amount', value: c.S,             color: '#2563eb' },
                { label: 'Contributions',   value: c.totalContrib,  color: '#10b981' },
                { label: 'Interest',        value: c.totalInterest, color: '#f97316' },
              ]}
              size={180}
            />
          </div>
          </>) : (<div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see your projected balance and schedule.</div>)}
        </div>
      </div>

      {/* Charts + schedule appear on ALL tabs — computed against the solved values */}
      {showResult && <div data-print="tables">
        <div className="calc-card p-5 mt-6">
          <h3 className="font-bold text-slate-900 mb-3">Balance Accumulation Graph</h3>
          <BarChart
            data={c.rows.map((r, i) => {
              const cumContrib = c.rows.slice(0, i + 1).reduce((a,b)=>a+b.deposit,0);
              const cumInt = c.rows.slice(0, i + 1).reduce((a,b)=>a+b.interest,0);
              return {
                label: String(r.year),
                values: [
                  { value: c.S,        color: '#2563eb', label: 'Starting Amount' },
                  { value: cumContrib, color: '#10b981', label: 'Contributions' },
                  { value: cumInt,     color: '#f97316', label: 'Interest' },
                ]
              };
            })}
            stack={true}
            legend={[
              { label: 'Starting Amount', color: '#2563eb' },
              { label: 'Contributions',   color: '#10b981' },
              { label: 'Interest',        color: '#f97316' },
            ]}
          />
        </div>

        <div className="calc-card p-5 mt-6">
          <h3 className="font-bold text-slate-900 mb-3">Accumulation Schedule</h3>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm" data-testid="inv-schedule-table">
              <thead className="bg-blue-800 text-white sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Year</th>
                  <th className="px-3 py-2 text-right">Deposit</th>
                  <th className="px-3 py-2 text-right">Interest</th>
                  <th className="px-3 py-2 text-right">Ending balance</th>
                </tr>
              </thead>
              <tbody>
                {c.rows.map((r,i)=>(
                  <tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}>
                    <td className="px-3 py-2">{r.year}</td>
                    <td className="px-3 py-2 text-right">{fmt(r.deposit)}</td>
                    <td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>}

      {showResult && <StepsPanel steps={[
        `Solved for <b>${c.solved.label}</b>: <b>${solvedDisplay}</b>.`,
        `Compound frequency: ${COMPOUND_OPTIONS.find(o=>o.key===compound)?.label}. Effective annual rate ≈ ${(effectiveAnnual(tab==='rate' ? c.computedRate : rate, compound)*100).toFixed(3)}%.`,
        `Contributions of ${fmt(c.C)} ${contribOpt.label} contributed at the <b>${contribWhen}</b> of each period for ~${c.Y.toFixed(2)} years grow to <b>${fmt(c.finalBalance)}</b>.`,
      ]} />}
    </CalcShell>
  );
}
